import { api } from '../../../core/utils/apiClient';
import {
  CompanyRecommendationsResponse,
  QueryContextRequest,
  QueryContextResponse,
  ComputeSimilaritiesRequest,
  ComputeSimilaritiesResponse,
} from '../types';

export class RecommendationsApiError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = 'RecommendationsApiError';
  }
}

class RecommendationsService {
  async getCompanyRecommendations(
    companyId: number,
    limit: number = 10,
    generateInsights: boolean = true
  ): Promise<CompanyRecommendationsResponse> {
    const response = await api.get(
      `/api/v1/recommendations/company/${companyId}?limit=${limit}&generate_insights=${generateInsights}`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new RecommendationsApiError(
        error?.detail || error?.message || 'Failed to load recommendations',
        response.status
      );
    }

    return response.json();
  }

  async getQueryContextRecommendations(
    query: QueryContextRequest
  ): Promise<QueryContextResponse> {
    const response = await api.post(
      `/api/v1/recommendations/query-context`,
      query
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to load query recommendations'
      );
    }

    return response.json();
  }

  async computeSimilarities(
    request: ComputeSimilaritiesRequest
  ): Promise<ComputeSimilaritiesResponse> {
    const response = await api.post(
      `/api/v1/recommendations/compute-similarities`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Failed to compute similarities'
      );
    }

    return response.json();
  }

  async checkHealth(): Promise<string> {
    const response = await api.get(`/api/v1/recommendations/health`);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error?.detail || error?.message || 'Health check failed'
      );
    }

    return response.json();
  }
}

export const recommendationsService = new RecommendationsService();
